import React, { useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  GRID_WIDTH,
  GRID_HEIGHT,
  CELL_SIZE,
  BLOCK_COLORS,
  isValidPosition,
} from '../../utils/tetris-utils';

const TetrisCanvas = ({ gameState }) => {
  const theme = useTheme();
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const width = GRID_WIDTH * CELL_SIZE;
    const height = GRID_HEIGHT * CELL_SIZE;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    // Clear
    ctx.fillStyle = theme.palette.mode === 'dark' ? '#0d1117' : '#f6f8fa';
    ctx.fillRect(0, 0, width, height);

    // Grid lines
    ctx.strokeStyle = theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.06)'
      : 'rgba(0,0,0,0.06)';
    ctx.lineWidth = 1;
    for (let x = 0; x <= GRID_WIDTH; x++) {
      ctx.beginPath();
      ctx.moveTo(x * CELL_SIZE, 0);
      ctx.lineTo(x * CELL_SIZE, height);
      ctx.stroke();
    }
    for (let y = 0; y <= GRID_HEIGHT; y++) {
      ctx.beginPath();
      ctx.moveTo(0, y * CELL_SIZE);
      ctx.lineTo(width, y * CELL_SIZE);
      ctx.stroke();
    }

    // Draw locked blocks
    gameState.grid.forEach((row, y) => {
      row.forEach((cell, x) => {
        if (cell) {
          drawBlock(ctx, x, y, cell.color, cell.word);
        }
      });
    });

    // Draw ghost piece
    if (gameState.currentPiece && gameState.status === 'playing') {
      let ghostY = gameState.currentPiece.y;
      while (isValidPosition(gameState.grid, gameState.currentPiece, 0, ghostY - gameState.currentPiece.y + 1)) {
        ghostY++;
      }
      if (ghostY !== gameState.currentPiece.y) {
        drawPiece(ctx, { ...gameState.currentPiece, y: ghostY }, true);
      }
    }

    // Draw current piece
    if (gameState.currentPiece) {
      drawPiece(ctx, gameState.currentPiece, false);
    }

    // Overlay for pause/gameover
    if (gameState.status === 'paused') {
      drawOverlay(ctx, width, height, 'PAUSED', 'rgba(0,0,0,0.7)');
    } else if (gameState.status === 'gameover') {
      drawOverlay(ctx, width, height, 'GAME OVER', 'rgba(180,0,0,0.8)');
    }
  }, [gameState, theme]);

  return (
    <Box
      sx={{
        display: 'inline-block',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 20px rgba(0,0,0,0.5)'
          : '0 4px 20px rgba(0,0,0,0.15)',
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block' }} />
    </Box>
  );
};

const drawBlock = (ctx, x, y, colorKey, word) => {
  const colors = BLOCK_COLORS[colorKey] || BLOCK_COLORS.cyan;
  const padding = 1;
  const bx = x * CELL_SIZE + padding;
  const by = y * CELL_SIZE + padding;
  const size = CELL_SIZE - padding * 2;

  // Fill
  ctx.fillStyle = colors.fill;
  ctx.fillRect(bx, by, size, size);

  // 3D effect - light edge
  ctx.fillStyle = 'rgba(255,255,255,0.25)';
  ctx.fillRect(bx, by, size, 3);
  ctx.fillRect(bx, by, 3, size);

  // 3D effect - dark edge
  ctx.fillStyle = 'rgba(0,0,0,0.25)';
  ctx.fillRect(bx, by + size - 3, size, 3);
  ctx.fillRect(bx + size - 3, by, 3, size);

  // Word text (if exists)
  if (word) {
    ctx.fillStyle = colors.text;
    ctx.font = `bold 10px -apple-system, BlinkMacSystemFont, 'Noto Sans KR', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(word.slice(0, 2), bx + size / 2, by + size / 2);
  }
};

const drawPiece = (ctx, piece, isGhost) => {
  const { shape, x, y, color, word } = piece;
  const colors = BLOCK_COLORS[color] || BLOCK_COLORS.cyan;

  shape.forEach((row, py) => {
    row.forEach((cell, px) => {
      if (cell && y + py >= 0) {
        const bx = (x + px) * CELL_SIZE + 1;
        const by = (y + py) * CELL_SIZE + 1;
        const size = CELL_SIZE - 2;

        if (isGhost) {
          ctx.strokeStyle = colors.fill;
          ctx.lineWidth = 2;
          ctx.setLineDash([3, 3]);
          ctx.strokeRect(bx, by, size, size);
          ctx.setLineDash([]);
        } else {
          // Fill
          ctx.fillStyle = colors.fill;
          ctx.fillRect(bx, by, size, size);

          // 3D edges
          ctx.fillStyle = 'rgba(255,255,255,0.3)';
          ctx.fillRect(bx, by, size, 3);
          ctx.fillRect(bx, by, 3, size);
          ctx.fillStyle = 'rgba(0,0,0,0.3)';
          ctx.fillRect(bx, by + size - 3, size, 3);
          ctx.fillRect(bx + size - 3, by, 3, size);
        }
      }
    });
  });

  // Draw word on center block (not ghost)
  if (!isGhost && word) {
    const centerX = x + Math.floor(shape[0].length / 2);
    const centerY = y + Math.floor(shape.length / 2);
    if (centerY >= 0) {
      ctx.fillStyle = colors.text;
      ctx.font = `bold 11px -apple-system, BlinkMacSystemFont, 'Noto Sans KR', sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(
        word,
        centerX * CELL_SIZE + CELL_SIZE / 2,
        centerY * CELL_SIZE + CELL_SIZE / 2
      );
    }
  }
};

const drawOverlay = (ctx, width, height, text, bgColor) => {
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold 20px -apple-system, BlinkMacSystemFont, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, width / 2, height / 2);
};

export default TetrisCanvas;
